import { createStore } from '@orderx/lab/xr/store';
import times from 'lodash/times';
import { MenuProduct } from '../XV2';
import { AppCache, useAppCacheAutoSync } from '../services/app-cache';
import { useEffect, useMemo } from 'react';
import { VariantGroupProduct } from '../XV2';
import { ModifierGroupItem } from '../XV2';
import { useDiscount } from './discount';

const STORAGE_KEY = 'useActiveOrder';

type OrderProductId = number;

type Store = {
    outletId: number;
    orderProductsById: Record<OrderProductId, ActiveOrderProduct>;
    animatedIds: number[];
    placedOrderId?: number;
};

export type ActiveOrderProduct = {
    id: number;
    productId: number;
    productDiscountId: number;
    menuProduct: MenuProduct;
    variantGroupProductId?: number;
    variantGroupProduct?: VariantGroupProduct;
    modifierGroups: {
        [modifierGroupId: number]: {
            [modifierGroupItemId: number]: ModifierGroupItem;
        };
    };
    comment?: string;
};

const { useStore } = createStore<Store>(
    Object.assign(
        {
            outletId: undefined,
            orderProductsById: {},
            animatedIds: [],
        } as Partial<Store>,
        AppCache.get(STORAGE_KEY),
    ),
);

function createOrderProduct({ id, menuProduct }: { id?: number; menuProduct: MenuProduct }): ActiveOrderProduct {
    const orderProduct: Partial<ActiveOrderProduct> = {
        id,
        menuProduct,
        comment: '',
        productId: menuProduct.productId,
        modifierGroups: {},
    };

    return orderProduct as any;
}

export function useActiveOrder() {
    const [state, setState] = useStore();
    const { getDiscountByProductId } = useDiscount();

    function setOutletId(outletId: number) {
        setState((state) => {
            state.outletId = outletId;
        });
    }

    const { orderProductsByProductId, orderProducts } = useMemo<{
        orderProductsByProductId: {
            [orderProductId: number]: ActiveOrderProduct[];
        };
        orderProducts: ActiveOrderProduct[];
    }>(() => {
        const orderProductsByProductId = {};
        const orderProducts: ActiveOrderProduct[] = [];

        for (const item of Object.values(state.orderProductsById)) {
            orderProductsByProductId[item.productId] = orderProductsByProductId[item.productId] || [];
            orderProductsByProductId[item.productId].push(item);
            orderProducts.push(item);
        }

        return { orderProductsByProductId, orderProducts };
    }, [state]);

    function resetActiveOrder() {
        const initialState: Store = {
            outletId: 0,
            orderProductsById: {},
            animatedIds: [],
            placedOrderId: undefined,
        };

        setState(() => initialState);
    }

    return {
        ...state,

        setState,

        createOrderProduct,

        hasActiveOrder: Boolean(state.placedOrderId) || orderProducts.length > 0,

        orderProducts: orderProducts.map((orderProduct) => {
            const discount = getDiscountByProductId(
                orderProduct.variantGroupProduct?.productId ?? orderProduct.productId,
            );

            return { ...orderProduct, productDiscountId: discount?.id };
        }) as ActiveOrderProduct[],

        getTotalProductCount() {
            return orderProducts.length;
        },

        hasProductsWithPrices() {
            return orderProducts.filter((item) => item.menuProduct.product.price > 0).length > 0;
        },

        addAnimatedId(animatedId: number) {
            setState((state) => {
                state.animatedIds = [...state.animatedIds, animatedId];
            });
        },

        resetAnimatedIds() {
            setState((state) => {
                state.animatedIds = [];
            });
        },

        getOrderProductById(id?: number): ActiveOrderProduct {
            return state.orderProductsById[id || 0];
        },

        addProduct(menuProduct: MenuProduct, amount: number) {
            setState((state) => {
                times(amount).forEach(() => {
                    const id = Math.random();
                    state.orderProductsById[id] = createOrderProduct({ id, menuProduct });
                });
            });
        },

        updateProduct(orderProduct: ActiveOrderProduct) {
            setState((state) => {
                const id = orderProduct.id || Math.random();
                state.orderProductsById[id] = { ...orderProduct, id };
            });
        },

        removeProduct(menuProduct: MenuProduct, amount: number) {
            setState((state) => {
                const orderProducts = Object.values(state.orderProductsById);
                const matches: number[] = [];

                times(amount).forEach(() => {
                    for (const orderProduct of orderProducts) {
                        if (matches.length < amount && orderProduct.productId === menuProduct.productId) {
                            matches.push(orderProduct.id);
                        }
                    }
                });

                for (const id of matches) {
                    delete state.orderProductsById[id];
                }
            });
        },

        removeOrderProductById(orderProductId: number) {
            setState((state) => {
                delete state.orderProductsById[orderProductId];
            });
        },

        clearOrderProducts() {
            setState((state) => {
                state.orderProductsById = {};
            });
        },

        getOrderProductsByProductId(orderProductId: number) {
            return orderProductsByProductId[orderProductId] || [];
        },

        getCountByProductId(orderProductId: number) {
            return (orderProductsByProductId[orderProductId] || []).length;
        },

        resetActiveOrder: resetActiveOrder,

        useRegister(outletId: number) {
            useEffect(() => {
                const isDifferentOutlet = state.outletId !== outletId;

                if (isDifferentOutlet) {
                    resetActiveOrder();
                    setOutletId(outletId);
                }
            }, [state.outletId, outletId]);

            useAppCacheAutoSync(STORAGE_KEY, state);
        },

        setPlacedOrderId(orderId: number) {
            setState((state) => {
                state.placedOrderId = orderId;
            });
        },

        resetPlacedOrderId() {
            setState((state) => {
                state.placedOrderId = undefined;
            });
        },
    };
}
